/**
 * Businesses on this install: who, how big, how used. Never what's inside.
 *
 * A temporary password comes back once, in the response that made or reset it.
 * It is held in this component's state only, and gone when the notice is
 * dismissed or the page is left. Those calls are plain awaits, not mutations,
 * so react-query's cache never holds the password either.
 */
import { useState, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { Card, Chip, DataTable, PageHeader } from "../components/ui";
import { day } from "../lib/format";
import { adminApi } from "./api";
import { lastSeen } from "./helpers";
import type { BusinessOut } from "./types";

const KEY = ["admin", "businesses"];

type Handover = { kind: "added"; business: string; owner: string; email: string; password: string };
type Reset = { email: string; password: string };

export function AdminBusinesses() {
  const queryClient = useQueryClient();
  const list = useQuery({ queryKey: KEY, queryFn: () => adminApi.businesses(), retry: false });
  const [problem, setProblem] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [reset, setReset] = useState<Reset | null>(null);
  const [editing, setEditing] = useState<{ id: string; draft: string } | null>(null);
  const [noteProblem, setNoteProblem] = useState<string | null>(null);

  const refresh = () => queryClient.invalidateQueries({ queryKey: KEY });

  async function act(id: string, work: () => Promise<void>) {
    setProblem(null);
    setBusyId(id);
    try {
      await work();
    } catch (err) {
      setProblem(err instanceof Error ? err.message : "Couldn't do that.");
    } finally {
      setBusyId(null);
    }
  }

  const toggle = (b: BusinessOut) => {
    if (
      b.is_active &&
      !window.confirm(`Switch off ${b.name}? Everyone in it is signed out straight away. Nothing is deleted.`)
    )
      return;
    void act(b.id, async () => {
      await adminApi.patchBusiness(b.id, { is_active: !b.is_active });
      await refresh();
    });
  };

  const resetOwner = (b: BusinessOut) => {
    const who = b.owner_name ?? b.owner_email ?? "the owner";
    if (!window.confirm(`Give ${who} a new one-time password? Their sessions end now.`)) return;
    setReset(null);
    void act(b.id, async () => {
      const r = await adminApi.resetOwner(b.id);
      setReset({ email: r.owner_email, password: r.temporary_password });
    });
  };

  const saveNote = (b: BusinessOut, draft: string) => {
    setNoteProblem(null);
    void act(b.id, async () => {
      try {
        await adminApi.patchBusiness(b.id, { note: draft });
        setEditing(null);
        await refresh();
      } catch (err) {
        setNoteProblem(err instanceof Error ? err.message : "Couldn't save the note.");
      }
    });
  };

  const editNote = (b: BusinessOut) => {
    setNoteProblem(null);
    setEditing({ id: b.id, draft: b.note ?? "" });
  };

  const chips = (b: BusinessOut) =>
    (!b.is_active || b.founding) && (
      <div className="btn-row" style={{ marginTop: 4 }}>
        {!b.is_active && <Chip tone="crit">Switched off</Chip>}
        {b.founding && <Chip>Set this install up</Chip>}
      </div>
    );

  const actions = (b: BusinessOut) => (
    <div className="adm-actions">
      <button className="btn btn-sm" type="button" disabled={busyId === b.id} onClick={() => toggle(b)}>
        {b.is_active ? "Switch off" : "Switch on"}
      </button>
      <button
        className="btn btn-sm"
        type="button"
        disabled={busyId === b.id || !b.owner_email}
        onClick={() => resetOwner(b)}
      >
        Reset owner's password
      </button>
      <button className="btn btn-sm" type="button" disabled={busyId === b.id} onClick={() => editNote(b)}>
        Note
      </button>
    </div>
  );

  const noteEditor = (b: BusinessOut) =>
    editing?.id === b.id && (
      <NoteEditor
        draft={editing.draft}
        busy={busyId === b.id}
        problem={noteProblem}
        onChange={(draft) => setEditing({ id: b.id, draft })}
        onSave={() => saveNote(b, editing.draft)}
        onCancel={() => setEditing(null)}
      />
    );

  const plan = (b: BusinessOut) =>
    b.plan ? (
      <>
        {b.plan}
        {b.paid_until && <div className="tiny muted">paid to {day(b.paid_until)}</div>}
      </>
    ) : (
      "—"
    );

  const rows = list.data ?? [];

  return (
    <div className="page">
      <PageHeader title="Businesses">
        Everyone on this Keystone. You see who and how big, never what's inside.
      </PageHeader>

      <AddBusiness onAdded={() => void refresh()} />

      {reset && (
        <Secret title="New one-time password" password={reset.password} onDone={() => setReset(null)}>
          <p>
            For <b className="adm-wrap">{reset.email}</b>.
          </p>
          <p className="tiny">They choose their own when they sign in. You won't see it.</p>
        </Secret>
      )}

      {problem && (
        <p className="notice notice-crit" role="alert">
          {problem}
        </p>
      )}

      {list.error ? (
        <Card>
          <p className="notice notice-crit" role="alert">
            {list.error.message}
          </p>
        </Card>
      ) : !list.data ? (
        <Card>
          <p className="tiny muted">Loading…</p>
        </Card>
      ) : (
        <>
          <div className="desk-only">
            <Card padded={false}>
              <DataTable
                columns={[
                  { key: "name", label: "Business" },
                  { key: "owner", label: "Owner" },
                  { key: "created", label: "Signed up" },
                  { key: "seen", label: "Last seen" },
                  { key: "people", label: "People", align: "right" },
                  { key: "jobs", label: "Jobs (30 days)", align: "right" },
                  { key: "plan", label: "Plan" },
                  { key: "note", label: "Note" },
                  { key: "act", label: "" },
                ]}
              >
                {rows.flatMap((b) => [
                  <tr key={b.id}>
                    <td>
                      <b>{b.name}</b>
                      {chips(b)}
                    </td>
                    <td>
                      {b.owner_name ?? "—"}
                      <div className="tiny muted">{b.owner_email}</div>
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>{day(b.created_at)}</td>
                    <td style={{ whiteSpace: "nowrap" }}>{lastSeen(b.last_sign_in_at)}</td>
                    <td className="right mono">{b.people}</td>
                    <td className="right mono">{b.jobs_30d}</td>
                    <td>{plan(b)}</td>
                    <td className="adm-note-cell" title={b.note ?? undefined}>
                      {b.note ?? "—"}
                    </td>
                    <td>{actions(b)}</td>
                  </tr>,
                  editing?.id === b.id ? (
                    <tr key={`${b.id}-note`}>
                      <td colSpan={9}>{noteEditor(b)}</td>
                    </tr>
                  ) : null,
                ])}
              </DataTable>
            </Card>
          </div>

          <div className="phone-only stack">
            {rows.map((b) => (
              <Card key={b.id} title={b.name}>
                {chips(b)}
                <p style={{ marginTop: 8 }}>{b.owner_name ?? "—"}</p>
                <p className="tiny muted adm-wrap">{b.owner_email}</p>
                <p className="tiny muted" style={{ marginTop: 6 }}>
                  Signed up {day(b.created_at)} · Last seen {lastSeen(b.last_sign_in_at)}
                </p>
                <p className="tiny muted">
                  {b.people} {b.people === 1 ? "person" : "people"} · {b.jobs_30d} job{b.jobs_30d === 1 ? "" : "s"} in
                  30 days
                </p>
                {b.plan && (
                  <p className="tiny muted">
                    Plan {b.plan}
                    {b.paid_until ? `, paid to ${day(b.paid_until)}` : ""}
                  </p>
                )}
                {b.note && editing?.id !== b.id && (
                  <p className="tiny adm-wrap" style={{ marginTop: 6 }}>
                    {b.note}
                  </p>
                )}
                {noteEditor(b)}
                <div style={{ marginTop: 10 }}>{actions(b)}</div>
              </Card>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function NoteEditor({
  draft,
  busy,
  problem,
  onChange,
  onSave,
  onCancel,
}: {
  draft: string;
  busy: boolean;
  problem: string | null;
  onChange: (value: string) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  return (
    <div className="adm-note-edit" style={{ marginTop: 8 }}>
      <textarea
        aria-label="Note"
        value={draft}
        maxLength={4000}
        onChange={(e) => onChange(e.target.value)}
        disabled={busy}
      />
      {problem && (
        <p className="notice notice-crit" role="alert">
          {problem}
        </p>
      )}
      <div className="btn-row">
        <button className="btn btn-primary btn-sm" type="button" disabled={busy} onClick={onSave}>
          {busy ? "Saving…" : "Save"}
        </button>
        <button className="btn btn-sm" type="button" disabled={busy} onClick={onCancel}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function AddBusiness({ onAdded }: { onAdded: () => void }) {
  const [form, setForm] = useState({ name: "", owner_name: "", owner_email: "" });
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [made, setMade] = useState<Handover | null>(null);

  const ready = form.name.trim() && form.owner_name.trim() && form.owner_email.trim();

  async function add() {
    if (busy || !ready) return;
    setBusy(true);
    setProblem(null);
    try {
      const r = await adminApi.addBusiness({
        name: form.name.trim(),
        owner_name: form.owner_name.trim(),
        owner_email: form.owner_email.trim(),
      });
      setMade({
        kind: "added",
        business: r.business.name,
        owner: r.business.owner_name ?? form.owner_name.trim(),
        email: r.business.owner_email ?? form.owner_email.trim(),
        password: r.temporary_password,
      });
      setForm({ name: "", owner_name: "", owner_email: "" });
      onAdded();
    } catch (err) {
      setProblem(err instanceof Error ? err.message : "Couldn't add the business.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title="Add a business">
      {made && (
        <div style={{ marginBottom: 12 }}>
          <Secret title={null} password={made.password} onDone={() => setMade(null)}>
            <p>
              <b>{made.business}</b> is ready. Give {made.owner} these to sign in:
            </p>
            <dl className="kv" style={{ margin: "8px 0" }}>
              <dt>Site</dt>
              <dd className="adm-wrap">{window.location.origin}</dd>
              <dt>Email</dt>
              <dd className="adm-wrap">{made.email}</dd>
            </dl>
            <p className="tiny">
              They'll choose their own password, then set up the business under Settings — including its own email.
            </p>
          </Secret>
        </div>
      )}
      <form
        onSubmit={(e) => {
          e.preventDefault();
          void add();
        }}
      >
        <div className="form-grid">
          <label className="field">
            <span>Business name</span>
            <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
          </label>
          <label className="field">
            <span>Owner's name</span>
            <input value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} />
          </label>
          <label className="field">
            <span>Owner's email</span>
            <input
              type="email"
              inputMode="email"
              autoCapitalize="none"
              autoCorrect="off"
              spellCheck={false}
              value={form.owner_email}
              onChange={(e) => setForm({ ...form, owner_email: e.target.value })}
            />
          </label>
        </div>
        {problem && (
          <p className="notice notice-crit" role="alert" style={{ marginTop: 10 }}>
            {problem}
          </p>
        )}
        <div className="btn-row" style={{ marginTop: 12 }}>
          <button className="btn btn-primary" type="submit" style={{ minHeight: 44 }} disabled={!ready || busy}>
            {busy ? "Adding…" : "Add business"}
          </button>
        </div>
      </form>
    </Card>
  );
}

/** A one-time password on screen, with Copy. Shown from the response only; kept nowhere. */
function Secret({
  title,
  password,
  onDone,
  children,
}: {
  title: string | null;
  password: string;
  onDone: () => void;
  children: ReactNode;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="notice notice-info" role="status">
      {title && (
        <p>
          <b>{title}</b>
        </p>
      )}
      {children}
      <div className="adm-secret" style={{ margin: "8px 0", color: "var(--text)" }}>
        {password}
      </div>
      <div className="btn-row">
        <button
          className="btn"
          type="button"
          style={{ minHeight: 44 }}
          onClick={() =>
            // navigator.clipboard is undefined over plain http; the password is
            // on screen either way, so a failed copy just leaves the label.
            void Promise.resolve()
              .then(() => navigator.clipboard.writeText(password))
              .then(() => setCopied(true))
              .catch(() => setCopied(false))
          }
        >
          {copied ? "Copied" : "Copy"}
        </button>
        <button className="btn" type="button" style={{ minHeight: 44 }} onClick={onDone}>
          Done
        </button>
      </div>
    </div>
  );
}
